/**
 * Орлогын график — дашбоардын гол блок.
 *
 * Муж нь URL-д хадгалагдана (`?range=week`) тул сэргээхэд алдагдахгүй,
 * холбоос хуваалцаж болно.
 *
 * ⚠️ Мөнгө string-ээр ирнэ. `Number()` руу хөрвүүлэх нь ЗӨВХӨН зурах
 *    координатад — харуулах дүн бүр `formatMnt`/`formatMntShort`-оор
 *    string дээр форматлагдана (CLAUDE.md дүрэм 3, 14).
 */

import { useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatMnt, formatMntShort, formatQty, isRevenueRange, type RevenueRange } from '@boots/domain';
import { useRevenueData } from '../../lib/use-revenue-data';
import { useRevenueStream, type StreamStatus } from '../../lib/use-revenue-stream';
import { Section } from '../section';
import { EmptyState, ErrorState } from '../empty-state';
import { Skeleton } from '../skeleton';
import { Tabs } from '../ui';
import { CountUp } from '../count-up';
import { RevenuePlot } from './revenue-plot';
import { bucketLabel, labelStepFor } from './revenue-labels';

const RANGES: { key: RevenueRange; label: string }[] = [
  { key: 'day', label: 'Өдөр' },
  { key: 'week', label: '7 хоног' },
  { key: 'month', label: 'Сар' },
  { key: 'year', label: 'Жил' },
];

const RANGE_NOTE: Record<RevenueRange, string> = {
  day: 'өмнөх 24 цагаас',
  week: 'өмнөх 7 хоногоос',
  month: 'өмнөх сараас',
  year: 'өмнөх оноос',
};

/** Оргил цэгийн тайлбар — мужаас хамаарч «өдөр», «цаг», «сар». */
const PEAK_NOTE: Record<RevenueRange, string> = {
  day: 'Оргил цаг',
  week: 'Оргил өдөр',
  month: 'Оргил өдөр',
  year: 'Оргил сар',
};

const DOT: Record<StreamStatus, { cls: string; title: string }> = {
  live: { cls: 'bg-accent', title: 'Шууд холбогдсон' },
  connecting: { cls: 'bg-warn-ink', title: 'Дахин холбогдож байна' },
  offline: { cls: 'bg-ink-3', title: 'Офлайн — 30 секунд тутам шинэчилнэ' },
};

export function RevenueChart() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('range');
  const range: RevenueRange = isRevenueRange(raw) ? raw : 'week';

  const [compare, setCompare] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const [flash, setFlash] = useState(false);

  const { data, isPending, error, refetch, applySale, refetchAll } = useRevenueData(range);

  const status = useRevenueStream({
    onSale: (amountMnt, occurredAt) => {
      applySale(amountMnt, occurredAt);
      setFlash(true);
      setTimeout(() => setFlash(false), 2_000);
    },
    onResync: refetchAll,
  });

  const setRange = (r: RevenueRange) => {
    const next = new URLSearchParams(params);
    if (r === 'week') next.delete('range');
    else next.set('range', r);
    setParams(next, { replace: true });
    setHover(null);
  };

  const r = data?.data;
  const values = r?.points.map((p) => Number(p.revenueMnt)) ?? [];
  const empty = r !== undefined && values.every((v) => v === 0);
  const pt = hover === null ? undefined : r?.points[hover];
  // Оргил — хамгийн өндөр орлоготой цэг. Зурж дууссаны дараа курсор энд сууна.
  const peak = values.length ? values.indexOf(Math.max(...values)) : null;
  const peakPt = peak === null ? undefined : r?.points[peak];

  return (
    <Section
      feature
      title="Орлого"
      badge={r ? `${formatQty(r.orderCount)} захиалга` : undefined}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <span
            aria-hidden
            title={DOT[status].title}
            className={`size-[7px] shrink-0 rounded-pill transition ${DOT[status].cls} ${
              flash ? 'animate-pulse ring-2 ring-accent/40' : ''
            }`}
          />
          <span className="sr-only" role="status">{DOT[status].title}</span>
          <button
            type="button"
            onClick={() => setCompare((v) => !v)}
            aria-pressed={compare}
            className={`inline-flex min-h-11 items-center gap-2 rounded-pill border px-3.5 py-1.5 text-meta
              transition active:scale-[0.97] sm:min-h-0 ${
              compare
                ? 'border-accent-300 bg-accent-100 text-ink'
                : 'border-hair text-ink-2 hover:text-ink'
            }`}
          >
            <span aria-hidden className="size-[7px] rounded-pill bg-accent" />
            Өмнөхтэй харьцуулах
          </button>
          <Tabs tabs={RANGES} value={range} onChange={setRange} />
        </div>
      }
    >
      {/* ── Нийт дүн + өөрчлөлт ─────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {isPending ? (
          <Skeleton className="h-12 w-56" />
        ) : (
          <span className="flex items-baseline gap-2.5 font-display text-num-xl font-semibold sm:text-num-2xl">
            <CountUp
              key={range}
              text={formatMntShort(r?.total)}
              unitClassName="font-sans text-title font-semibold tracking-normal text-ink-3"
            />
          </span>
        )}
        {r?.changePercent === null || r === undefined ? null : (
          <span
            key={`${range}-${r.changePercent >= 0}`}
            style={{ '--delay': '600ms' } as CSSProperties}
            className={`pop-in rounded-[14px] border px-3 py-1.5 text-body font-semibold whitespace-nowrap ${
              r.changePercent >= 0
                ? 'border-accent-300 bg-accent-100 text-accent-700'
                : 'border-alert/35 bg-alert-bg text-alert-ink'
            }`}
          >
            <span aria-hidden className="mr-1">{r.changePercent >= 0 ? '▲' : '▼'}</span>
            {Math.abs(r.changePercent).toFixed(1)}% {RANGE_NOTE[range]}
          </span>
        )}
      </div>

      {/* ── График ──────────────────────────────────────────── */}
      {error ? (
        <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />
      ) : isPending ? (
        <Skeleton className="h-[180px] w-full rounded-well sm:h-[240px]" />
      ) : empty ? (
        <EmptyState
          compact
          title="Энэ хугацаанд борлуулалт бүртгэгдээгүй байна"
          description="Кассаар борлуулалт бүртгэгдмэгц график шууд шинэчлэгдэнэ."
        />
      ) : (
        <div className="flex flex-col gap-2">
          <div className="relative mt-6 h-[180px] sm:h-[230px]">
            <RevenuePlot
              values={values}
              compare={compare ? r?.previousPoints.map(Number) : undefined}
              hover={hover}
              onHover={setHover}
              height={230}
              peak={peak}
              tip={
                pt ? (
                  <>
                    <span className="text-micro text-ink-2">{bucketLabel(range, pt.bucket, 'long')}</span>
                    <span className="num text-h3">{formatMnt(pt.revenueMnt)}</span>
                    <span className="text-micro text-ink-2">{formatQty(pt.orderCount)} захиалга</span>
                  </>
                ) : peakPt ? (
                  <>
                    <span className="text-label text-ink-3">{PEAK_NOTE[range]}</span>
                    <span className="text-h3 font-bold">{bucketLabel(range, peakPt.bucket, 'long')}</span>
                  </>
                ) : null
              }
            />
          </div>

          {/* X тэнхлэг — HTML, SVG биш (сунгагдахгүй, кирилл уншигдана) */}
          <div className="flex text-label font-semibold text-ink-3" aria-hidden>
            {r?.points.map((p, i) => {
              const step = labelStepFor(range, r.points.length);
              // Захын шошго нүднээсээ хальж картын хүрээ рүү гарахгүй
              const cell = 'min-w-0 flex-1 text-center first:text-left last:text-right';
              if (i % step !== 0) return <span key={p.bucket} className={cell} />;
              // 8-аас олон шошго 320px дээр багтахгүй («Жил» = 12). Нүдийг
              // ҮЛДЭЭЖ, текстийг нь CSS-ээр нэг алгасаж нууна — дэсктоп хэвээр.
              const dense = r.points.length / step > 8;
              return (
                <span key={p.bucket} className={`${cell} transition-colors ${i === peak ? 'text-accent-700' : ''}`}>
                  <span className={dense && (i / step) % 2 === 1 ? 'hidden sm:inline' : undefined}>
                    {bucketLabel(range, p.bucket, 'short')}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
}
